import React, { memo, useId } from 'react';
import { StyleSheet, Text as RNText, View } from 'react-native';
import Svg, { ClipPath, Defs, G, Path } from 'react-native-svg';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import Text from 'design-system/Text';
import { IconLock } from 'design-system/icons/IconLock';
import useColors from 'hooks/useColors';
import { formatCount } from '../utils';
const BADGE_SIZE = rem(44);
// Rounded pentagons in a 44x44 box, from the Figma quest badge: an outer
// shape, an inner one with a light inner border, and a lighter fill on top
const OUTER_PATH = 'M18.84 1.89c1.88-1.37 4.44-1.37 6.32 0l15.68 11.39c1.88 1.37 2.67 3.8 1.95 6.01l-5.99 18.43c-.72 2.22-2.78 3.72-5.11 3.72H12.31c-2.33 0-4.39-1.5-5.11-3.72L1.2 19.29c-.72-2.21.07-4.64 1.96-6.01L18.84 1.89Z';
const INNER_PATH = 'M20.59 6.27c.84-.61 1.97-.61 2.81 0l13.9 10.1c.84.61 1.19 1.69.87 2.67l-5.31 16.34c-.32.99-1.24 1.65-2.27 1.65H13.4c-1.03 0-1.95-.66-2.27-1.65L5.82 19.04c-.32-.98.03-2.06.87-2.67l13.9-10.1Z';
const INNER_LIGHT_PATH = 'M20.6 6.27c.84-.61 1.97-.61 2.81 0l13.9 10.1c.84.61 1.19 1.69.87 2.67l-5.31 16.34c-.32.99-1.24 1.65-2.27 1.65H13.41c-1.03 0-1.95-.66-2.27-1.65L5.83 19.04c-.32-.98.03-2.06.87-2.67l13.9-10.1Z';
// Darker area at the bottom right, clipped to the outer shape
const SHADOW_PATH = 'M36.38 15.69 11.7 36.27l16.58 16.58 42.71-2.47-34.61-34.69Z';
// White glare at the top left
const GLARE_PATH = 'M11.34 13.6 17.17 9.37';
const StreakBadgeCard = ({ badge }) => {
    const colors = useColors();
    const id = useId();
    const count = `${formatCount(badge.days)} day`;
    const label = `${count} streak`;
    const baseColor = badge.isUnlocked
        ? colors.gradients.darkPink.colors[0]
        : colors.elements.secondaryLight;
    // Locked badges are a translucent grey, as in the Figma
    const baseOpacity = badge.isUnlocked ? 1 : 0.5;
    return (<CardView noMargin style={staticStyles.card} testID={`streak-badge-${badge.days}`} accessibilityLabel={`${label}, ${badge.isUnlocked ? 'unlocked' : 'locked'}`}>
      <View style={staticStyles.badge}>
        <Svg width={BADGE_SIZE} height={BADGE_SIZE} viewBox="0 0 44 44" style={StyleSheet.absoluteFill}>
          <Defs>
            <ClipPath id={`outer_${id}`}>
              <Path d={OUTER_PATH}/>
            </ClipPath>
            <ClipPath id={`inner_${id}`}>
              <Path d={INNER_PATH}/>
            </ClipPath>
          </Defs>
          <Path d={OUTER_PATH} fill={baseColor} fillOpacity={baseOpacity}/>
          {badge.isUnlocked ? (<G clipPath={`url(#outer_${id})`}>
              <Path d={SHADOW_PATH} fill={colors.elements.black} fillOpacity={0.1}/>
            </G>) : null}
          <Path d={INNER_PATH} fill={baseColor} fillOpacity={baseOpacity} stroke={colors.elements.white} strokeOpacity={0.2} strokeWidth={2.4} clipPath={`url(#inner_${id})`}/>
          <Path d={INNER_LIGHT_PATH} fill={colors.elements.white} fillOpacity={0.6}/>
          {badge.isUnlocked ? (<Path d={GLARE_PATH} stroke={colors.elements.white} strokeOpacity={0.6} strokeWidth={0.98} strokeLinecap="round"/>) : null}
        </Svg>
        {badge.isUnlocked ? (<RNText style={staticStyles.emoji}>{badge.emoji}</RNText>) : (
        // Wrapped so web stacks it above the absolutely positioned shape
        <View>
            <IconLock width={17} height={17} colorKey="secondaryLight"/>
          </View>)}
      </View>
      {/* Always two lines, so labels line up across the grid */}
      <Text Text-14 centered numberOfLines={2}>
        {`${count}\nstreak`}
      </Text>
    </CardView>);
};
const staticStyles = StyleSheet.create({
    card: {
        flex: 1,
        alignItems: 'center',
        gap: rem(8),
        paddingHorizontal: rem(12),
        paddingVertical: rem(16),
    },
    badge: {
        width: BADGE_SIZE,
        height: BADGE_SIZE,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emoji: {
        fontSize: rem(14),
        lineHeight: rem(20),
        marginTop: rem(1.5),
    },
});
export default memo(StreakBadgeCard);
